/**
 * ProductScannerModal — reusable universal product scanner.
 *
 * Three ways to identify a product:
 *   1. Live camera barcode/QR decode  (ZXing multi-format reader)
 *   2. Manual UPC/EAN/GTIN entry       (typed input, no camera needed)
 *   3. Photo + AI vision fallback     (existing /api/label-scan/analyze)
 *
 * When a barcode is found we hit /api/products/by-barcode/{code}. When
 * the AI extracts a name we search /api/food-items?q=<name>. If we get
 * a real match we call onMatch(product). If nothing matches we surface
 * a "Submit this product" flow that posts to /api/pending-products so
 * the item enters the crowdsource queue.
 *
 * Callers:
 *   <ProductScannerModal open={open} onClose={close}
 *                        onMatch={(product) => …} />
 *
 * Compatible with mobile (getUserMedia with {facingMode: 'environment'})
 * and desktop webcams.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Camera, X, Keyboard, Loader2, ScanLine, CheckCircle2,
  AlertTriangle, Sparkles, Send, Image as ImageIcon, RefreshCw,
} from "lucide-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const MODES = { CAMERA: "camera", MANUAL: "manual", PHOTO: "photo" };

export default function ProductScannerModal({
  open,
  onClose,
  onMatch,          // (product, characteristics) => void
  showSubmit = true,
  initialMode = "camera",
}) {
  const { user } = useAuth();
  const [mode, setMode] = useState(MODES.CAMERA);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notFound, setNotFound] = useState(null);     // { code, guess?, image_url? }
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Sync initialMode changes coming in from the caller.
  useEffect(() => {
    if (open) setMode(initialMode || MODES.CAMERA);
  }, [open, initialMode]);

  // ── Cleanup camera when modal closes ─────────────────────────
  const stopCamera = useCallback(() => {
    try { controlsRef.current?.stop(); } catch { /* ignore */ }
    try {
      const stream = videoRef.current?.srcObject;
      if (stream) stream.getTracks?.().forEach((t) => t.stop());
    } catch { /* ignore */ }
    controlsRef.current = null;
    setScanning(false);
  }, []);

  useEffect(() => {
    if (!open) {
      stopCamera();
      setMode(MODES.CAMERA);
      setLastScannedCode(null);
      setManualCode("");
      setPhotoFile(null);
      setPhotoPreview(null);
      setNotFound(null);
      setBusy(false);
    }
  }, [open, stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // ── Camera live-scan ──────────────────────────────────────────
  const startCamera = useCallback(async () => {
    if (!videoRef.current || scanning) return;
    setNotFound(null);
    setScanning(true);
    try {
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      controlsRef.current = await reader.decodeFromConstraints(
        {
          video: {
            facingMode: { ideal: "environment" },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        },
        videoRef.current,
        async (result, err, controls) => {
          if (result) {
            const code = result.getText().trim();
            try { controls.stop(); } catch { /* ignore */ }
            controlsRef.current = null;
            setScanning(false);
            setLastScannedCode(code);
            await lookupCode(code);
          }
        },
      );
    } catch (e) {
      setScanning(false);
      toast.error(e?.message?.includes("Permission")
        ? "Camera permission denied. Try manual UPC entry instead."
        : "Couldn't start the camera. Try manual entry.");
      setMode(MODES.MANUAL);
    }
  }, [scanning]);

  useEffect(() => {
    if (open && mode === MODES.CAMERA && !scanning) {
      startCamera();
    }
    if (mode !== MODES.CAMERA) stopCamera();
  }, [open, mode, scanning, startCamera, stopCamera]);

  // ── Lookup a decoded / typed barcode ─────────────────────────
  async function lookupCode(code) {
    if (!code) return;
    setBusy(true);
    setNotFound(null);
    try {
      const r = await http.get(`/products/by-barcode/${encodeURIComponent(code)}`);
      const { product, characteristics } = r.data;
      toast.success(`Matched: ${product?.common_name || product?.slug}`);
      onMatch?.({ ...product, barcode: code }, characteristics);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setNotFound({ code, message: "This barcode isn't in our database yet." });
      } else if (status === 400) {
        toast.error("Invalid barcode checksum — please retry.");
      } else {
        toast.error(e?.response?.data?.detail || "Lookup failed");
      }
    } finally {
      setBusy(false);
    }
  }

  // ── Manual UPC entry ─────────────────────────────────────────
  async function submitManual(e) {
    e?.preventDefault?.();
    const code = manualCode.trim();
    if (!code) return;
    setLastScannedCode(code);
    await lookupCode(code);
  }

  // ── Photo → AI vision fallback ───────────────────────────────
  function pickPhoto(f) {
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("Please pick an image");
      return;
    }
    setPhotoFile(f);
    setNotFound(null);
    const rdr = new FileReader();
    rdr.onload = () => setPhotoPreview(rdr.result);
    rdr.readAsDataURL(f);
  }

  async function runAiScan() {
    if (!photoFile) return;
    setBusy(true);
    setAiUnavailable(false);
    try {
      const form = new FormData();
      form.append("file", photoFile);
      const r = await http.post(`/label-scan/analyze`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = r.data;
      // Try to resolve the AI product name against food_items catalog.
      const guessName = data?.product_name || data?.title || null;
      if (guessName) {
        try {
          const search = await http.get(
            `/food-items`, { params: { q: guessName, limit: 1 } },
          );
          const hit = (search.data.items || search.data || [])[0];
          if (hit) {
            toast.success(`AI matched: ${hit.name || hit.common_name}`);
            onMatch?.({
              id: hit.id, slug: hit.slug,
              common_name: hit.name || hit.common_name,
              hero_image: hit.hero_image || hit.image_url,
              category: hit.category,
            });
            onClose?.();
            return;
          }
        } catch { /* fallthrough to not-found */ }
      }
      // No catalog match — offer to submit as pending.
      setNotFound({
        code: null,
        guess: guessName,
        image_url: photoPreview,
        message: guessName
          ? `AI thinks this is "${guessName}", but it isn't in our catalog yet.`
          : "We couldn't identify this product.",
        ai_extracted: data,
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429 || (e?.response?.data?.detail || "").toString().toLowerCase().includes("limit")) {
        setAiUnavailable(true);
        toast.error("AI scan is temporarily unavailable (daily limit). Try manual UPC or submit a photo.");
      } else {
        toast.error(e?.response?.data?.detail || "AI scan failed");
      }
    } finally {
      setBusy(false);
    }
  }

  // ── Submit not-found product to pending queue ────────────────
  const [submitName, setSubmitName] = useState("");
  const [submitBrand, setSubmitBrand] = useState("");
  const [submitNotes, setSubmitNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (notFound?.guess && !submitName) setSubmitName(notFound.guess);
  }, [notFound, submitName]);

  async function submitPending() {
    if (!user) {
      toast.error("Please sign in to submit new products.");
      return;
    }
    if (!submitName.trim()) {
      toast.error("Give the product a name so admins can review it.");
      return;
    }
    setSubmitting(true);
    try {
      let imageUrls = [];
      // Upload the captured photo (if any) to Object Storage so admins
      // and the eventual food_items row can display it.
      if (photoFile) {
        const form = new FormData();
        form.append("file", photoFile);
        const up = await http.post("/lists/upload-image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (up.data?.url) imageUrls.push(up.data.url);
      }
      await http.post("/pending-products", {
        name:          submitName.trim(),
        brand:         submitBrand.trim() || undefined,
        barcode:       notFound?.code || lastScannedCode || undefined,
        image_urls:    imageUrls,
        notes:         submitNotes.trim() || undefined,
        scan_source:   notFound?.code ? "manual-upc" : (photoFile ? "photo" : "camera"),
        ai_extracted:  notFound?.ai_extracted || undefined,
      });
      toast.success("Thanks! Your submission is in the review queue.");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur"
      role="dialog" aria-modal="true"
      data-testid="product-scanner-modal"
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[95vh] flex flex-col"
        style={{ color: GREEN }}
      >
        {/* Header ------------------------------------------------- */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#EED9AC" }}>
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5" style={{ color: AMBER }} />
            <div className="font-semibold">Scan a product</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-black/5"
            data-testid="scanner-close"
            aria-label="Close scanner"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode switcher ------------------------------------------ */}
        <div className="flex gap-1 px-3 pt-3">
          {[
            { k: MODES.CAMERA, l: "Camera", icon: Camera },
            { k: MODES.MANUAL, l: "Type UPC", icon: Keyboard },
            { k: MODES.PHOTO,  l: "Photo (AI)", icon: Sparkles },
          ].map(({ k, l, icon: I }) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition
                          ${mode === k ? "text-white" : "opacity-60 hover:opacity-100 bg-black/5"}`}
              style={mode === k ? { background: GREEN } : {}}
              data-testid={`scanner-mode-${k}`}
            >
              <I className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>

        {/* Body --------------------------------------------------- */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {notFound ? (
            <SubmitPendingBlock
              notFound={notFound}
              photoPreview={photoPreview}
              name={submitName} setName={setSubmitName}
              brand={submitBrand} setBrand={setSubmitBrand}
              notes={submitNotes} setNotes={setSubmitNotes}
              submitting={submitting}
              onSubmit={submitPending}
              onRetry={() => { setNotFound(null); setPhotoFile(null); setPhotoPreview(null); }}
              showSubmit={showSubmit}
            />
          ) : mode === MODES.CAMERA ? (
            <div>
              <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay muted playsInline
                  data-testid="scanner-video"
                />
                {/* Reticle overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-3/4 h-1/3 border-2 rounded-lg" style={{ borderColor: AMBER }} />
                </div>
                {scanning && (
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Scanning…
                  </div>
                )}
                {busy && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-sm">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Looking up…
                  </div>
                )}
              </div>
              <p className="text-xs opacity-70 mt-3 text-center">
                Point at the barcode or QR code on the package.
                <br />No barcode?{" "}
                <button className="underline font-semibold" onClick={() => setMode(MODES.PHOTO)}>
                  Try photo (AI)
                </button>{" or "}
                <button className="underline font-semibold" onClick={() => setMode(MODES.MANUAL)}>
                  type it in
                </button>.
              </p>
            </div>
          ) : mode === MODES.MANUAL ? (
            <form onSubmit={submitManual}>
              <label className="block text-xs font-mono uppercase tracking-widest opacity-70 mb-2">
                UPC / EAN / GTIN
              </label>
              <input
                type="text" inputMode="numeric" autoFocus
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 012000161551"
                className="w-full rounded-2xl border px-4 py-3 text-lg font-mono tracking-widest outline-none focus:ring-2"
                style={{ borderColor: "#E9D8B4" }}
                data-testid="scanner-manual-input"
              />
              <button
                type="submit"
                disabled={busy || !manualCode.trim()}
                className="w-full mt-4 rounded-full px-4 py-3 font-semibold text-white disabled:opacity-40"
                style={{ background: GREEN }}
                data-testid="scanner-manual-submit"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Looking up…</> : "Look up product"}
              </button>
              <p className="text-xs opacity-60 mt-2 text-center">
                We validate the checksum (Mod-10) and match against 885+ products.
              </p>
            </form>
          ) : (
            /* PHOTO mode ------------------------------------- */
            <div>
              {photoPreview ? (
                <div className="mb-3">
                  <img src={photoPreview} alt="preview" className="w-full rounded-2xl object-cover max-h-72" />
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="mt-2 text-xs opacity-70 hover:opacity-100 flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" /> Change photo
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full rounded-2xl border-2 border-dashed p-8 flex flex-col items-center gap-2 hover:bg-amber-50"
                  style={{ borderColor: AMBER, color: GREEN }}
                  data-testid="scanner-pick-photo"
                >
                  <ImageIcon className="w-8 h-8" style={{ color: AMBER }} />
                  <div className="font-semibold">Take or pick a photo</div>
                  <div className="text-xs opacity-70">AI will read the label / identify the item</div>
                </button>
              )}
              <input
                ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                className="hidden"
                onChange={(e) => pickPhoto(e.target.files?.[0])}
                data-testid="scanner-photo-input"
              />
              {aiUnavailable && (
                <div className="mt-3 rounded-xl p-3 text-xs flex items-start gap-2"
                     style={{ background: "#FEF3C7", color: "#78350F" }}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    AI scan hit today's usage limit — please try the <b>Camera</b> or <b>Type UPC</b> tab, or submit the photo as a new-product suggestion below.
                  </div>
                </div>
              )}
              {photoFile && (
                <button
                  onClick={runAiScan}
                  disabled={busy}
                  className="w-full mt-3 rounded-full px-4 py-3 font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ background: GREEN }}
                  data-testid="scanner-run-ai"
                >
                  {busy
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Identifying…</>
                    : <><Sparkles className="w-4 h-4" /> Identify with AI</>}
                </button>
              )}
              {photoFile && !busy && (
                <button
                  onClick={() => setNotFound({ code: null, message: "Submit this photo as a new product." })}
                  className="w-full mt-2 rounded-full px-4 py-2 text-sm font-semibold border"
                  style={{ borderColor: GREEN, color: GREEN }}
                >
                  Skip AI · Submit this photo
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmitPendingBlock({
  notFound, photoPreview,
  name, setName, brand, setBrand, notes, setNotes,
  submitting, onSubmit, onRetry, showSubmit,
}) {
  return (
    <div>
      <div className="rounded-2xl p-3 mb-4 flex items-start gap-3"
           style={{ background: "#FEF3C7", color: "#78350F" }}>
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold">{notFound.message}</div>
          {notFound.code && (
            <div className="opacity-80 text-xs mt-1 font-mono">Code: {notFound.code}</div>
          )}
        </div>
      </div>
      {photoPreview && (
        <img src={photoPreview} alt="" className="w-full max-h-40 object-cover rounded-xl mb-3" />
      )}
      {!showSubmit ? (
        <button onClick={onRetry} className="w-full rounded-full py-2 border font-semibold" style={{ borderColor: GREEN, color: GREEN }}>
          Try again
        </button>
      ) : (
        <>
          <div className="mb-3">
            <label className="block text-[11px] font-mono uppercase tracking-widest opacity-70 mb-1">Product name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Great Value Organic Peanut Butter 500g"
                   className="w-full rounded-xl border px-3 py-2 outline-none"
                   style={{ borderColor: "#E9D8B4" }}
                   data-testid="pending-submit-name" />
          </div>
          <div className="mb-3">
            <label className="block text-[11px] font-mono uppercase tracking-widest opacity-70 mb-1">Brand (optional)</label>
            <input value={brand} onChange={(e) => setBrand(e.target.value)}
                   className="w-full rounded-xl border px-3 py-2 outline-none"
                   style={{ borderColor: "#E9D8B4" }}
                   data-testid="pending-submit-brand" />
          </div>
          <div className="mb-4">
            <label className="block text-[11px] font-mono uppercase tracking-widest opacity-70 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Anything that helps our team review this…"
                      className="w-full rounded-xl border px-3 py-2 outline-none"
                      style={{ borderColor: "#E9D8B4" }}
                      data-testid="pending-submit-notes" />
          </div>
          <div className="flex gap-2">
            <button onClick={onRetry}
                    className="rounded-full px-4 py-2 text-sm font-semibold border"
                    style={{ borderColor: GREEN, color: GREEN }}
                    data-testid="pending-submit-retry">
              Try again
            </button>
            <button onClick={onSubmit} disabled={submitting || !name.trim()}
                    className="flex-1 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
                    style={{ background: GREEN }}
                    data-testid="pending-submit-btn">
              {submitting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                : <><Send className="w-4 h-4" /> Submit for review</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
